import { useEffect, useRef, useState } from "react";
import className from "classnames/bind";
import { AnimatePresence, motion } from "motion/react";
import { easeOutQuad } from "@/core/easing";
import InfoIcon from "@/assets/svg/info.svg";
import { isTouchDevice } from "@/core/utils";

import styles from "./ImageAttribution.module.scss";
import { usePageWrapperData } from "@/providers/PageWrapperContext";
const cx = className.bind(styles);

interface ImageAttributionProps {
  position?: "left" | "right" | "center";
  text: string;
  className?: string;
}

export default function ImageAttribution({
  position = "right",
  text,
  className,
}: ImageAttributionProps) {
  const [showAttribution, setShowAttribution] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { labels } = usePageWrapperData();
  const xPos = position === "center" ? "-50%" : 0;
  const handleMouseEnter = () => {
    if (isTouchDevice()) return;
    setShowAttribution(true);
  }
  const handleMouseLeave = () => {
    if (isTouchDevice()) return;
    setShowAttribution(false);
  };
  const handleFocus = () => {
    if (isTouchDevice()) return;
    setShowAttribution(true);
  }
  const handleBlur = () => setShowAttribution(false);
  const checkClick = (e: React.MouseEvent) => {
    if (!isTouchDevice()) return;
    e.stopPropagation();
    setShowAttribution((prev) => !prev);
  }

  useEffect(() => {
    if (!showAttribution || !isTouchDevice()) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (buttonRef.current?.contains(e.target as Node)) return;
      setShowAttribution(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [showAttribution]);

  return (
    <button
      ref={buttonRef}
      className={cx(["image-attribution", className])}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur} 
      onClick={checkClick}
      aria-label={labels?.ariaLabels?.imageAttribution}
      type="button"
    >
      <div className={cx("image-attribution__icon-wrapper")}>
        <InfoIcon className={cx("icon")} />
      </div>
      <AnimatePresence>
        {showAttribution && (
          <motion.div
            key="attribution-box"
            className={cx("image-attribution__attribution", position)}
            initial={{ opacity: 0, y: 10, x: xPos }}
            animate={{ opacity: 1, y: 0, x: xPos }}
            exit={{ opacity: 0, y: 10, x: xPos }}
            transition={{ duration: 0.3, ease: easeOutQuad }}
          >
            <div className="text-s" dangerouslySetInnerHTML={{ __html: text }} />
          </motion.div>
        )}
      </AnimatePresence>
    </button>
  );
}
